// Bid Size bands filter by the LOW BIDDER's adjusted total on each proposal (the
// winning bid), so the user picks the size of jobs by their awarded value. The
// bands are multi-select: each selected band, plus an optional custom range,
// contributes one window and the windows are OR'd together (so non-adjacent
// picks stay exact, with no gap filled in). This module is the single source of
// truth shared by the filter UI and every repository's bid-amount predicate.

export type BidSizeBand = {
  id: string;
  label: string;
  min: number;
  max: number | null;
};

export const BID_SIZE_BANDS: BidSizeBand[] = [
  { id: '0-5', label: '$0 to $5M', min: 0, max: 5_000_000 },
  { id: '5-10', label: '$5M to $10M', min: 5_000_000, max: 10_000_000 },
  { id: '10-25', label: '$10M to $25M', min: 10_000_000, max: 25_000_000 },
  { id: '25-100', label: '$25M to $100M', min: 25_000_000, max: 100_000_000 },
  { id: '100+', label: '$100M and greater', min: 100_000_000, max: null },
];

export type BidAmountRange = { min: number; max: number | null };

// Resolve the selected band ids + an optional custom range into the list of
// (min, max) windows to match. A custom range with only one bound is open on the
// other side. Unknown band ids are ignored. Empty list = no bid-size filter.
export function resolveBidSizeRanges(
  bandIds: string[] | undefined | null,
  customMin: number | null | undefined,
  customMax: number | null | undefined,
): BidAmountRange[] {
  const ranges: BidAmountRange[] = [];
  for (const id of bandIds ?? []) {
    const band = BID_SIZE_BANDS.find((b) => b.id === id);
    if (band) {
      ranges.push({ min: band.min, max: band.max });
    }
  }
  if (customMin != null || customMax != null) {
    ranges.push({ min: customMin ?? 0, max: customMax ?? null });
  }
  return ranges;
}

// ── Wire format: explicit OR'd ranges ─────────────────────────────────────────
//
// The filter travels as the literal ranges, NOT band ids — one `min-max` segment
// per window, comma-joined, open max = trailing empty. e.g. "$0–5M + $100M+" →
// `0-5000000,100000000-`. This is the single representation on the URL and the
// API; the backend ORs the windows. Bands are just UI presets that expand here.

export function encodeBidSizeRanges(ranges: BidAmountRange[]): string {
  return ranges.map((r) => `${r.min}-${r.max ?? ''}`).join(',');
}

// Parse the wire form back to ranges. Accepts the comma string (URL / GET) or an
// already-structured array (JSON POST body). Invalid segments are dropped; an
// empty/absent value yields no ranges (no bid-size filter).
export function parseBidSizeRanges(
  value: string | BidAmountRange[] | null | undefined,
): BidAmountRange[] {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    // Drop entries whose min isn't a real number, or whose max is present but
    // not a real number — a NaN must never survive as a filter (it would match
    // everything via `min: 0` or break the SQL via `<= NaN`). Mirrors the
    // string-segment validation below.
    return value
      .map((r) => ({
        min: Number(r.min),
        max: r.max == null ? null : Number(r.max),
      }))
      .filter(
        (r) =>
          Number.isFinite(r.min) && (r.max === null || Number.isFinite(r.max)),
      );
  }
  const ranges: BidAmountRange[] = [];
  for (const seg of value.split(',')) {
    if (!seg) {
      continue;
    }
    const dash = seg.indexOf('-');
    if (dash === -1) {
      continue;
    }
    const minStr = seg.slice(0, dash);
    const maxStr = seg.slice(dash + 1);
    const min = minStr ? Number(minStr) : 0;
    const max = maxStr ? Number(maxStr) : null;
    if (!Number.isFinite(min) || (max != null && !Number.isFinite(max))) {
      continue;
    }
    ranges.push({ min, max });
  }
  return ranges;
}

// Split a ranges list back into the picker's UI state: a range equal to a band's
// (min, max) checks that band; the first leftover range fills the custom inputs.
// The inverse of `resolveBidSizeRanges` — used to rehydrate the filter UI from
// the URL.
export function rangesToBandSelection(ranges: BidAmountRange[]): {
  bandIds: string[];
  customMin: number | null;
  customMax: number | null;
} {
  const bandIds: string[] = [];
  let customMin: number | null = null;
  let customMax: number | null = null;
  for (const r of ranges) {
    const band = BID_SIZE_BANDS.find((b) => b.min === r.min && b.max === r.max);
    if (band) {
      bandIds.push(band.id);
    } else if (customMin == null && customMax == null) {
      customMin = r.min || null;
      customMax = r.max;
    }
  }
  return { bandIds, customMin, customMax };
}

// Render a dollar bound in the band-label idiom, using explicit math so the
// output is identical on ANY Node/ICU build (compact `Intl.NumberFormat` rounds
// differently across ICU versions). `$0` stays `$0` to match the `$0 to $5M`
// band label exactly; `>= $1M` reads as `$NM`, `$1K–$1M` as `$NK`, and below
// `$1K` as plain `$N`. The magnitude keeps up to one decimal with a trailing
// `.0` trimmed, so whole values stay clean (`$5M`, `$750K`) while fractional
// ones show one place (`$2.5M`).
function trimDecimal(value: number): string {
  return Number.parseFloat(value.toFixed(1)).toString();
}

// Suffixes for each 1000× magnitude, smallest first: '' (<$1K), K, M, B. A bound
// is shown against the largest unit it clears, then promoted again if rounding to
// one decimal pushes the magnitude to 1000 of that unit (e.g. 999_999 → 999.999K
// → "1000K" should read as "$1M"). The top suffix is a hard ceiling — values that
// large don't occur for bid bounds and simply stay in B.
type BidUnit = { suffix: string; divisor: number };

const BID_UNITS: BidUnit[] = [
  { suffix: '', divisor: 1 },
  { suffix: 'K', divisor: 1_000 },
  { suffix: 'M', divisor: 1_000_000 },
  { suffix: 'B', divisor: 1_000_000_000 },
];

function formatBidBound(amount: number): string {
  if (amount === 0) {
    return '$0';
  }
  // Walk the ladder small→large, keeping the largest unit the raw amount clears
  // (>= its divisor). The plain (<$1K) unit always clears, so a unit is always
  // chosen. After settling, if one-decimal rounding tips the magnitude to 1000 of
  // that unit, promote to the next one so `$1000K` reads as `$1M` (and cascades
  // M→B); the top unit is a ceiling with no promotion.
  let unit: BidUnit = { suffix: '', divisor: 1 };
  for (let i = 0; i < BID_UNITS.length; i++) {
    const candidate = BID_UNITS[i];
    if (!candidate || amount < candidate.divisor) {
      continue;
    }
    unit = candidate;
    const next = BID_UNITS[i + 1];
    if (next && Number(trimDecimal(amount / candidate.divisor)) >= 1000) {
      unit = next;
    }
  }
  return `$${trimDecimal(amount / unit.divisor)}${unit.suffix}`;
}

// A display label for one explicit bid-size range, in the same idiom the bands
// use. An exact band match reuses the band's canonical label (`$0 to $5M`,
// `$100M and greater`); a custom range formats as `$X to $Y` (or `$X and
// greater` for an open max), so the user's bounds read consistently whether or
// not they line up with a preset band.
export function bidSizeRangeLabel(range: BidAmountRange): string {
  const band = BID_SIZE_BANDS.find(
    (b) => b.min === range.min && b.max === range.max,
  );
  if (band) {
    return band.label;
  }
  const low = formatBidBound(range.min);
  if (range.max === null) {
    return `${low} and greater`;
  }
  return `${low} to ${formatBidBound(range.max)}`;
}
